import React from 'react';

import type { FieldError } from 'react-hook-form';

import { classnames, padding } from '@lumapps/classnames';
import { TextField, TextFieldProps } from '@lumapps/lumx/react';
import { useTranslate, GLOBAL } from '@lumapps/translations';

import { useFormField } from '../../hooks/useFormField';
import { BaseInput } from '../../types';
import { DefaultValueWrapper } from '../DefaultValueWrapper';

export type FormTextFieldProps = Omit<TextFieldProps, 'onChange' | 'value'> &
    BaseInput & {
        displayValidityOnBlur?: boolean;
        // You need a validation schema that returns error per language to use it.
        // Only language specific errors will be displayed then.
        displayLanguageSpecificErrors?: boolean;
        type?: 'number' | 'text' | 'date';
    };

/**
 * A text field component that can be added into a Form in order to be used with react-hook-form.
 * @family Forms
 * @param FormTextFieldProps
 * @returns FormTextField
 */
export const FormTextField: React.FC<FormTextFieldProps> = ({
    name,
    isRequired,
    controllerProps,
    isMultiLanguage = false,
    preventEmptyFields = false,
    languagesFormat,
    displayValidityOnBlur = false,
    label,
    type = 'text',
    displayLanguageSpecificErrors,
    ...props
}) => {
    const { translateKey } = useTranslate();

    const [wasBlurred, setWasBlurred] = React.useState(false);
    const {
        valueToUse,
        field,
        onMultilanguageChange,
        fieldState,
        defaultValueWrapperProps,
        isFieldDisabled,
        inputProps,
        getDataAttributes,
        language,
    } = useFormField<string | number>({
        name,
        controllerProps,
        isMultiLanguage,
        preventEmptyFields,
        isRequired,
        languagesFormat,
        label,
    });

    const hasSingleError = (!isMultiLanguage || !displayLanguageSpecificErrors) && Boolean(fieldState.error);
    const hasErrorInLanguage =
        isMultiLanguage &&
        displayLanguageSpecificErrors &&
        Boolean((fieldState.error as any as Record<string, FieldError> | undefined)?.[language]);
    const hasError = hasSingleError || hasErrorInLanguage;
    const hasValue = valueToUse && valueToUse !== '';
    const errorMessageToUse = hasErrorInLanguage
        ? (fieldState.error as any as Record<string, FieldError> | undefined)?.[language].message
        : fieldState.error?.message || translateKey(GLOBAL.FIELD_IS_REQUIRED);

    return (
        <DefaultValueWrapper {...defaultValueWrapperProps}>
            <TextField
                inputRef={field.ref}
                value={valueToUse?.toString() || ''}
                name={field.name}
                isRequired={isRequired}
                label={label}
                aria-invalid={hasError}
                type={type}
                onBlur={() => {
                    if (isMultiLanguage) {
                        onMultilanguageChange(valueToUse);
                    }

                    field.onBlur();

                    setWasBlurred(true);
                }}
                onFocus={() => setWasBlurred(false)}
                hasError={hasError}
                error={errorMessageToUse}
                /**
                 * We only want to display that the field is valid when the user has made some changes to the field
                 * and has already left it. We can determine this by checking the `isDirty` and `isTouched` values
                 * at the same time.
                 */
                isValid={
                    wasBlurred && displayValidityOnBlur && hasValue && fieldState.isDirty && fieldState.isTouched
                        ? !hasError
                        : undefined
                }
                {...getDataAttributes({ action: name, element: 'text-field' })}
                {...props}
                onChange={(val) => {
                    const value = type === 'number' ? parseInt(val, 10) : val;

                    if (isMultiLanguage) {
                        onMultilanguageChange(value);
                    } else {
                        field.onChange(value);
                    }
                }}
                isDisabled={props.isDisabled || isFieldDisabled}
                {...inputProps}
                aria-disabled={inputProps['aria-disabled'] || props['aria-disabled']}
                className={classnames(props.className, padding('vertical', null))}
            />
        </DefaultValueWrapper>
    );
};
